"use client";

import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Text,
  Badge,
  Icon,
} from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import { useAuth, useRoleSwitch } from "@/lib/contexts";
import { useMemo, useRef, useState, type RefObject } from "react";
import {
  getRoleIcon,
  getRoleDisplayName,
  getRoleColorScheme,
  getSwitchableAdminRoles,
  resolveVisibleActiveRole,
} from "@/lib/utils/roles";
import { HiOutlineCheck } from "react-icons/hi2";
import { TouchAwareTooltip } from "@/components/ui/TouchAwareTooltip";
import { useStudioFeatureSettings } from "@/features/dashboard/hooks/useStudioFeatureSettings";

interface RoleSwitcherProps {
  size?: "sm" | "md" | "lg";
}

const sizeMap = {
  sm: { w: "32px", h: "32px", iconSize: "16px" },
  md: { w: "40px", h: "40px", iconSize: "20px" },
  lg: { w: "48px", h: "48px", iconSize: "24px" },
};

export const RoleSwitcher = ({ size = "sm" }: RoleSwitcherProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const user = useAuth();
  const roleSwitch = useRoleSwitch();
  const updateRole = roleSwitch?.updateRole ?? (async () => {});
  const isLoading = roleSwitch?.isSwitching ?? false;

  const sizeStyles = sizeMap[size];
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const activeMenuItemRef = useRef<HTMLButtonElement>(null);
  const initialFocusRef = activeMenuItemRef as RefObject<{ focus(): void }>;

  const { choreographerPortalEnabled, isPending } = useStudioFeatureSettings();
  const portalKnown = !isPending;
  const adminRoles = useMemo(
    () =>
      getSwitchableAdminRoles(user, {
        choreographerPortalEnabled,
        choreographerPortalKnown: portalKnown,
      }),
    [choreographerPortalEnabled, portalKnown, user],
  );
  const activeRole =
    resolveVisibleActiveRole(user, choreographerPortalEnabled, portalKnown) ??
    adminRoles[0] ??
    null;
  const hasMultipleRoles = adminRoles.length > 1;

  if (!hasMultipleRoles || !activeRole) {
    return null;
  }

  const ActiveRoleIcon = getRoleIcon(activeRole);
  const activeRoleColorScheme = getRoleColorScheme(activeRole);

  const handleRoleSelect = async (role: string) => {
    if (role === activeRole || isLoading || !roleSwitch) return;
    setIsMenuOpen(false);
    try {
      await updateRole(role);
    } catch (error) {
      console.error("Failed to update role:", error);
    }
  };

  return (
    <Menu
      placement="bottom-end"
      closeOnSelect={true}
      isLazy
      lazyBehavior="unmount"
      autoSelect={false}
      initialFocusRef={initialFocusRef}
      isOpen={isMenuOpen}
      onOpen={() => setIsMenuOpen(true)}
      onClose={() => setIsMenuOpen(false)}
    >
      <TouchAwareTooltip
        label="Switch role"
        placement="bottom"
        isDisabled={isMenuOpen}
      >
        <MenuButton
        as={Box}
        position="relative"
        w={sizeStyles.w}
        h={sizeStyles.h}
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderWidth="1px"
        borderColor={theme.borderGlass.default}
        color={theme.text.secondary}
        borderRadius="8px"
        transition="all 0.18s ease-out"
        cursor={isLoading ? "not-allowed" : "pointer"}
        disabled={isLoading}
        sx={{
          "@media (hover: hover)": {
            "&:hover:not(:disabled)": {
              bg: theme.surfaceGlass.cardStrong,
              borderColor: theme.accent.primaryBorder,
              color: theme.accent.primary,
            },
          },
          "@media (hover: none) and (pointer: coarse)": {
            "&:active:not(:disabled)": {
              transform: "scale(0.95)",
            },
          },
        }}
        _focus={{
          boxShadow: theme.focusRing,
          outline: "none",
        }}
        opacity={isLoading ? 0.85 : 1}
      >
        <Box position="relative" display="flex" alignItems="center" justifyContent="center">
          <Icon as={ActiveRoleIcon} boxSize={sizeStyles.iconSize} />
          <Badge
            position="absolute"
            top="-6px"
            right="-6px"
            colorScheme={activeRoleColorScheme}
            fontSize="9px"
            fontWeight="700"
            borderRadius="full"
            minW="16px"
            h="16px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            px="3px"
            boxShadow="0 2px 6px rgba(0,0,0,0.25)"
            borderWidth="2px"
            borderColor={theme.surfaceGlass.card}
            zIndex={1}
            textTransform="uppercase"
          >
            {activeRole.charAt(0)}
          </Badge>
        </Box>
      </MenuButton>
      </TouchAwareTooltip>

      <Portal>
        <MenuList
          key={activeRole}
          w="200px"
          borderWidth="1px"
          borderColor={theme.borderGlass.default}
          py={2}
          overflowY="auto"
          zIndex={2000}
          bg={"none"}
          backdropFilter="blur(14px)"
          borderRadius="8px"
          boxShadow={theme.shadow.card}
        >
          <Box px={3} py={2}>
            <Text
              fontSize="xs"
              fontWeight="600"
              color={theme.text.tertiary}
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              Switch Role
            </Text>
          </Box>
          
          {adminRoles.map((role) => {
            const RoleIcon = getRoleIcon(role);
            const isActive = role === activeRole;
            const roleColorScheme = getRoleColorScheme(role);

            return (
              <MenuItem
                key={role}
                ref={isActive ? activeMenuItemRef : undefined}
                onClick={() => handleRoleSelect(role)}
                isDisabled={isLoading}
                bg={isActive ? theme.accent.mutedBg : "transparent"}
                color={isActive ? theme.accent.primary : theme.text.primary}
                sx={{
                  "@media (hover: hover)": {
                    "&:hover": {
                      bg: isActive
                        ? theme.accent.mutedBg
                        : interactive.neutral.bg,
                    },
                  },
                }}
                _focus={{
                  bg: isActive ? theme.accent.mutedBg : "transparent",
                  boxShadow: "none",
                }}
                py={2}
                px={3}
              >
                <Flex align="center" gap={3} w="100%">
                  <Icon
                    as={RoleIcon}
                    boxSize={5}
                    color={
                      isActive
                        ? theme.accent.primary
                        : theme.text.secondary
                    }
                  />
                  <Text
                    fontSize="sm"
                    fontWeight={isActive ? "600" : "500"}
                    flex="1"
                  >
                    {getRoleDisplayName(role)}
                  </Text>
                  {isActive && (
                    <Icon
                      as={HiOutlineCheck}
                      boxSize={4}
                      color={theme.accent.primary}
                    />
                  )}
                </Flex>
              </MenuItem>
            );
          })}
        </MenuList>
      </Portal>
    </Menu>
  );
};